/**
 * SanctaLauncher — the floating rail-tab / pill launcher for the Sancta widget.
 * Extracted from SanctaChatbot.tsx to keep that file lean.
 *
 * Position is driven by the parent's `useDragPersist` hook and passed in as
 * `drag`. When the user drags, the button morphs from an anchored right-edge
 * rail-tab into a free-floating pill.
 */
import React from "react";
import { MessageCircle, X } from "lucide-react";

type DragHandle = {
  pos: { dragged: boolean; x: number; y: number };
  wasClick: boolean;
  draggableProps: any;
};

interface Props {
  open: boolean;
  drag: DragHandle;
  onToggle: () => void;
  chatbotName: string;
}

export const SanctaLauncher: React.FC<Props> = ({ open, drag, onToggle, chatbotName }) => {
  const positionStyle = drag.pos.dragged
    ? {
        position: "fixed" as const,
        left: drag.pos.x,
        top: drag.pos.y,
        right: "auto" as const,
        bottom: "auto" as const,
        transform: "none",
      }
    : {
        position: "fixed" as const,
        right: 0,
        top: "50%",
        transform: "translateY(-50%)",
      };

  return (
    <button
      data-testid="sancta-launcher"
      data-dragged={drag.pos.dragged || undefined}
      onClick={() => {
        // Suppress the click that ends a drag — useDragPersist flips
        // `wasClick=false` after real movement.
        if (!drag.wasClick) return;
        onToggle();
      }}
      aria-label={open ? `Close ${chatbotName}` : `Open ${chatbotName} assistant`}
      {...drag.draggableProps}
      style={{ ...drag.draggableProps.style, ...positionStyle }}
      className={`z-[95] h-14 shadow-lg shadow-black/25 grid place-items-center select-none cursor-grab active:cursor-grabbing transition-colors
                  ${drag.pos.dragged ? "w-14 rounded-full" : "w-12 rounded-l-2xl rounded-r-none"}
                  ${open ? "bg-neutral-900 text-white" : "bg-gradient-to-br from-orange-500 to-fuchsia-600 text-white hover:scale-[1.03]"}`}
    >
      {open
        ? <X className="h-6 w-6 pointer-events-none" />
        : <MessageCircle className="h-6 w-6 pointer-events-none" />
      }
    </button>
  );
};

export default SanctaLauncher;
